/**
 * Wired as of 2026-08-23. `Player.tsx` renders this instead of the <video>
 * stage whenever `useMpvPlayback` reports active — see `lib/useMpvPlayback.ts`
 * for the eligibility matrix, which is unit-tested.
 */
import { useEffect, useRef } from "react";
import { isDesktop } from "../lib/desktop";

/**
 * Placeholder that reserves the video area in the desktop shell.
 *
 * In the desktop app the picture is painted by mpv in a native overlay window,
 * not by a `<video>` element — that is what lets it play MKV / HEVC / AC-3
 * straight from the source URL with no server-side transmuxing. This component
 * occupies the layout slot and continuously reports its on-screen rectangle so
 * the shell can keep the overlay aligned.
 *
 * It reports on scroll, resize and any layout change (ResizeObserver), because
 * a native child window keeps absolute screen coordinates and does not move
 * with the page. Renders a plain black box in a browser, where it is unused.
 */
export function DesktopVideoSurface({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDesktop() || !ref.current) return;
    const el = ref.current;
    const video = window.brainzr!.video;

    let raf = 0;
    const report = () => {
      cancelAnimationFrame(raf);
      // Coalesce bursts (a scroll fires many events per frame) into one IPC
      // call per frame; each one repositions a native window.
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        if (r.width < 2 || r.height < 2) {
          void video.hide();
          return;
        }
        void video.setBounds({ x: r.left, y: r.top, width: r.width, height: r.height });
      });
    };

    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    window.addEventListener("scroll", report, true);
    window.addEventListener("resize", report);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("scroll", report, true);
      window.removeEventListener("resize", report);
      void video.hide();
    };
  }, []);

  // No background. Under the hole-punch arrangement mpv's window sits BEHIND
  // the main window and shows through this rectangle, so any paint here — even
  // black — occludes the video exactly the way Chromium's opaque compositor
  // surface did inside the overlay. It also stays correct under the older
  // always-on-top overlay arrangement, where mpv covers this element anyway.
  return <div ref={ref} className={className ?? "absolute inset-0"} />;
}
